import { LinearGradient } from 'expo-linear-gradient';
import { DESIGN_TOKENS } from '@/lib/design/theme';
import { subscribeToReduceMotionChange } from '@/lib/app/nativeEventSubscriptions';
import * as React from 'react';
import { AccessibilityInfo, Animated, Easing, View } from 'react-native';

export function AuthGradient() {
  const driftA = React.useRef(new Animated.Value(0)).current;
  const driftB = React.useRef(new Animated.Value(0)).current;
  const [reduceMotionEnabled, setReduceMotionEnabled] = React.useState(false);

  React.useEffect(() => {
    let mounted = true;

    AccessibilityInfo.isReduceMotionEnabled()
      .then((enabled) => {
        if (mounted) {
          setReduceMotionEnabled(enabled);
        }
      })
      .catch(() => undefined);

    const unsubscribe = subscribeToReduceMotionChange(setReduceMotionEnabled);

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  React.useEffect(() => {
    if (reduceMotionEnabled) {
      driftA.stopAnimation();
      driftB.stopAnimation();
      return;
    }

    const animation = Animated.loop(
      Animated.parallel([
        Animated.sequence([
          Animated.timing(driftA, {
            toValue: 1,
            duration: 9000,
            easing: Easing.inOut(Easing.sin),
            useNativeDriver: true,
          }),
          Animated.timing(driftA, {
            toValue: 0,
            duration: 9000,
            easing: Easing.inOut(Easing.sin),
            useNativeDriver: true,
          }),
        ]),
        Animated.sequence([
          Animated.timing(driftB, {
            toValue: 1,
            duration: 12000,
            easing: Easing.inOut(Easing.sin),
            useNativeDriver: true,
          }),
          Animated.timing(driftB, {
            toValue: 0,
            duration: 12000,
            easing: Easing.inOut(Easing.sin),
            useNativeDriver: true,
          }),
        ]),
      ])
    );

    animation.start();
    return () => animation.stop();
  }, [driftA, driftB, reduceMotionEnabled]);

  const warmTransform = {
    transform: [
      {
        translateY: driftA.interpolate({
          inputRange: [0, 1],
          outputRange: [-10, 14],
        }),
      },
      {
        translateX: driftB.interpolate({
          inputRange: [0, 1],
          outputRange: [10, -12],
        }),
      },
    ],
  } as const;

  const coolTransform = {
    transform: [
      {
        translateX: driftA.interpolate({
          inputRange: [0, 1],
          outputRange: [-14, 18],
        }),
      },
      {
        translateY: driftB.interpolate({
          inputRange: [0, 1],
          outputRange: [8, -10],
        }),
      },
    ],
  } as const;

  return (
    <View
      pointerEvents="none"
      style={{
        position: 'absolute',
        inset: 0,
        backgroundColor: DESIGN_TOKENS.colors.canvas,
      }}>
      <LinearGradient
        colors={[
          DESIGN_TOKENS.colors.sceneGradientStart,
          DESIGN_TOKENS.colors.sceneGradientMid,
          DESIGN_TOKENS.colors.sceneGradientEnd,
        ]}
        start={{ x: 0, y: 0 }}
        end={{ x: 0, y: 1 }}
        style={{
          position: 'absolute',
          inset: 0,
        }}
      />
      {!reduceMotionEnabled ? (
        <>
          <Animated.View
            style={{
              position: 'absolute',
              inset: 0,
              opacity: 0.16,
              ...coolTransform,
            }}>
            <LinearGradient
              colors={[
                DESIGN_TOKENS.colors.ambientCool,
                'rgba(255,255,255,0.01)',
                'rgba(255,255,255,0)',
              ]}
              start={{ x: 0, y: 0.25 }}
              end={{ x: 1, y: 0.75 }}
              style={{
                position: 'absolute',
                inset: -24,
              }}
            />
          </Animated.View>
          <Animated.View
            style={{
              position: 'absolute',
              inset: 0,
              opacity: 0.12,
              ...warmTransform,
            }}>
            <LinearGradient
              colors={[
                'rgba(255,255,255,0)',
                DESIGN_TOKENS.colors.ambientWarm,
                'rgba(255,255,255,0)',
              ]}
              start={{ x: 0.5, y: 0 }}
              end={{ x: 0.5, y: 1 }}
              style={{
                position: 'absolute',
                inset: -18,
              }}
            />
          </Animated.View>
        </>
      ) : null}
      <LinearGradient
        colors={[
          DESIGN_TOKENS.colors.ambientOverlay,
          'rgba(255,255,255,0.02)',
        ]}
        start={{ x: 0.5, y: 0 }}
        end={{ x: 0.5, y: 1 }}
        style={{
          position: 'absolute',
          inset: 0,
        }}
      />
    </View>
  );
}
